import { computed, Ref } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import { isAudioFile, isImageFile, isStereoFile, isVideoFile } from "@/lib/fileHelpers";

export function getDuration(file: MessageAttachment | ContentFile | FileData | null | undefined) {
  if (!file) return 0;
  return Number(
    "file" in file ? file.file.metadata.duration : "fileId" in file ? file.metadata.duration : file.duration
  );
}

export default function useAttachmentFilesInfo(files: Ref<Array<MessageAttachment | ContentFile | FileData>>) {
  const getFile = (file: MessageAttachment | ContentFile | FileData) => ("file" in file ? file.file : file);
  const getFileType = (file: ContentFile | FileData) => {
    if (isImageFile(file)) return "images";
    if (isVideoFile(file)) return "videos";
    if (isAudioFile(file)) return "audio";
    if (isStereoFile(file)) return "vr";
    return "documents";
  };
  const processedFiles = computed(() => {
    return files.value.reduce<{
      documents: (ContentFile | FileData)[];
      images: (ContentFile | FileData)[];
      videos: (ContentFile | FileData)[];
      audio: (ContentFile | FileData)[];
      vr: (ContentFile | FileData)[];
    }>(
      (out, f) => {
        const key = getFileType(getFile(f));
        out[key].push(getFile(f));
        return out;
      },
      {
        documents: [],
        images: [],
        videos: [],
        audio: [],
        vr: [],
      }
    );
  });

  const showDuration = computed(() => {
    return processedFiles.value.videos.length > 0;
  });

  const totalVideoFilesDuration = computed(() => {
    if (!showDuration.value) return "";
    const result = files.value.reduce((acc, attachment) => {
      if (isVideoFile(getFile(attachment))) acc += getDuration(attachment);
      return acc;
    }, 0);
    return String(result);
  });

  const imageCount = computed(() => processedFiles.value.images.length);
  const videoCount = computed(() => processedFiles.value.videos.length);
  const audioCount = computed(() => processedFiles.value.audio.length);
  const documentCount = computed(() => processedFiles.value.documents.length);
  const vrCount = computed(() => processedFiles.value.vr.length);

  return {
    totalVideoFilesDuration,
    imageCount,
    videoCount,
    audioCount,
    documentCount,
    vrCount,
  };
}
